import { Component, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Observable } from 'rxjs';
import { filter } from 'rxjs/operators';

import { FinancingHold, Invoice, Permission } from 'src/app/api/models';
import { InvoicePeerService } from 'src/app/service/invoice-peer.service';
import { UserService } from 'src/app/service/user.service';
import { financingHoldScopeLabel } from 'src/app/shared/financing-holds/financing-hold-text';

import {
  PlaceFinancingHoldDialogComponent,
  PlaceFinancingHoldDialogData,
} from './place-financing-hold-dialog/place-financing-hold-dialog.component';
import {
  ReleaseFinancingHoldDialogComponent,
  ReleaseFinancingHoldDialogData,
} from './release-financing-hold-dialog/release-financing-hold-dialog.component';
import {
  ReleaseFinancingHoldsDialogComponent,
  ReleaseFinancingHoldsDialogData,
} from './release-financing-holds-dialog/release-financing-holds-dialog.component';

/**
 * The invoice page's financing-holds panel (add-financing-holds, design
 * D13): the active holds that apply to the invoice (`financing_holds`), a
 * Place dialog and a Release dialog per hold. From two holds up it also
 * offers "Release holds", a dialog that releases several of them with one
 * note (place-and-release-several-financing-holds, design D6); with one
 * hold the row's own Release does the same.
 *
 * Place and both releases are gated on the permissions the endpoints check,
 * the way the state widget gates Reset: disabled with a tooltip naming the
 * missing permission, never on a role name. Sales holds place but not
 * release.
 *
 * After a place or a release the widget reloads the invoice through the
 * peer service, so this panel and the page's hold chips update together.
 */
@Component({
  selector: 'app-invoice-financing-holds-widget',
  templateUrl: './invoice-financing-holds-widget.component.html',
  styleUrls: ['./invoice-financing-holds-widget.component.scss'],
})
export class InvoiceFinancingHoldsWidgetComponent implements OnInit {
  public invoice$: Observable<Invoice>;
  // Emit nothing until the current user loads, so the template treats null
  // (unknown) apart from false (denied), as the state widget's Reset does.
  public canPlace$: Observable<boolean>;
  public canRelease$: Observable<boolean>;

  public readonly placePermissionTooltip = `Requires the ${Permission.FinancingHoldsPlace} permission`;
  public readonly releasePermissionTooltip = `Requires the ${Permission.FinancingHoldsRelease} permission`;

  public readonly scopeLabel = financingHoldScopeLabel;

  constructor(
    private invoicePeerService: InvoicePeerService,
    private userService: UserService,
    private dialog: MatDialog,
  ) {}

  public ngOnInit(): void {
    this.invoice$ = this.invoicePeerService.getInvoice().pipe(filter((invoice) => !!invoice));
    this.canPlace$ = this.userService.hasPermission(Permission.FinancingHoldsPlace);
    this.canRelease$ = this.userService.hasPermission(Permission.FinancingHoldsRelease);
  }

  public subtitle(invoice: Invoice): string {
    const count = invoice.financing_holds?.length ?? 0;
    return count > 0 ? `On financing hold (${count})` : 'None';
  }

  public place(invoice: Invoice): void {
    this.dialog
      .open<PlaceFinancingHoldDialogComponent, PlaceFinancingHoldDialogData, FinancingHold[]>(
        PlaceFinancingHoldDialogComponent,
        { maxWidth: '520px', data: { invoice } },
      )
      .afterClosed()
      // The dialog closes with the placed holds, or undefined on cancel.
      .pipe(filter((holds) => !!holds && holds.length > 0))
      .subscribe(() => this.reload(invoice));
  }

  public release(invoice: Invoice, hold: FinancingHold): void {
    this.dialog
      .open<ReleaseFinancingHoldDialogComponent, ReleaseFinancingHoldDialogData, FinancingHold>(
        ReleaseFinancingHoldDialogComponent,
        { maxWidth: '520px', data: { hold } },
      )
      .afterClosed()
      .pipe(filter((released) => !!released))
      .subscribe(() => this.reload(invoice));
  }

  public releaseSeveral(invoice: Invoice): void {
    this.dialog
      .open<ReleaseFinancingHoldsDialogComponent, ReleaseFinancingHoldsDialogData, FinancingHold[]>(
        ReleaseFinancingHoldsDialogComponent,
        { maxWidth: '520px', data: { holds: invoice.financing_holds ?? [] } },
      )
      .afterClosed()
      // The dialog closes with the released holds, or undefined on cancel.
      .pipe(filter((released) => !!released && released.length > 0))
      .subscribe(() => this.reload(invoice));
  }

  public trackByHoldId(_index: number, hold: FinancingHold): number {
    return hold.id;
  }

  private reload(invoice: Invoice): void {
    this.invoicePeerService.initialize(invoice.id);
  }
}
